/**
 * 두톤 아이콘 세트 — 그룹웨어 업무 모듈 (2026-08-26)
 *
 * 출처: web-design-style 아카이브의 **재료함**(`icons:icon-3 · 16 · 17 · 21 · 52 · 58`,
 * 원본 sample:moaworks-ai-board / moaworks-icon-set, Hiworks 계열 SVG). 형은 한 점도
 * 바꾸지 않았고 **고정색 두 개만 토큰으로** 바꿔 끼웠다.
 *
 * ── 왜 currentColor 가 아니라 변수 두 개인가 ───────────────────────────────
 * 이 세트는 몸통(`#354154`)과 강조(`#2a88c9`) **두 색으로 그려져** 있어서 `currentColor`
 * 하나로는 두 톤이 한 덩어리가 된다. 그래서 두 색을 `--duo-ink` · `--duo-key` 로 바꿔 두고,
 * 쓰는 쪽이 상태에 따라 그 둘만 덮는다(꺼짐/켜짐). 형·좌표·선 굵기는 원본 그대로다.
 *
 * ── 원본에서 걷어낸 것: id ────────────────────────────────────────────────
 * 원본에는 그리기 도구가 남긴 `id="사각형_8461"` 같은 것이 붙어 있는데, **서로 겹친다**
 * (워크스페이스와 게시판이 같은 이름을 쓴다). 한 문서에 여러 개를 그리면 중복 id 가 되고,
 * 같은 아이콘을 두 번 그려도 마찬가지다. 이 여섯은 `url(#...)` 로 자기 id 를 참조하지 않아
 * **그냥 지웠다** — 접두를 붙이는 것보다 확실하다.
 *   ⚠ 참조를 쓰는 아이콘(clipPath 등)을 나중에 이 세트에 들일 때는 지우지 말고 **접두**해야
 *     한다. 아카이브의 `icons:icon-4`(프로젝트)·`icons:icon-18`(AI회의록)이 그런 경우로,
 *     둘 다 `id="clip-path"` 를 써서 그대로 두면 뒤엣것이 앞엣것의 클립에 잘려 사라진다.
 *
 * ── 루트 fill 을 옮겨 온 것 ──────────────────────────────────────────────
 * 손으로 그린 확장본(알림·인사)은 `fill="none"` 을 **루트 `<svg>` 에 한 번만** 두고 자식은
 * `stroke` 만 갖는다. 루트를 갈아 끼우면서 그 속성을 빠뜨리면 `fill` 이 초기값 `black` 으로
 * 돌아가 **획만 있어야 할 도형 안쪽이 통째로 칠해진다**. 아래 컴포넌트들은 루트에 그대로 싣는다.
 */

export interface DuotoneIconProps {
  /** 한 변(px). 이 세트는 28px 로 그려져 있어 16px 아래로 줄이면 두 톤이 뭉갠다. */
  size?: number;
  className?: string;
  /** 호출부가 관용적으로 붙이는 것 — 껍데기가 이미 `aria-hidden` 을 달므로 받아만 두고 쓰지 않는다. */
  'aria-hidden'?: boolean | 'true' | 'false';
}

/** 공통 껍데기 — viewBox·크기·접근성 처리를 한곳에 모은다(장식이므로 aria-hidden). */
function Duo({ size = 18, className, viewBox = '0 0 28 28', children }: DuotoneIconProps & {
  viewBox?: string; children: React.ReactNode;
}): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox={viewBox}
      fill="none"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

const INK = 'var(--duo-ink, currentColor)';
const KEY = 'var(--duo-key, currentColor)';
/** 아이콘 안쪽을 파낸 자리(체크·줄) — 뒤에 깔린 바탕색을 그대로 쓴다. */
const PAPER = 'var(--duo-paper, #fff)';

/** 회사 — 재료함에 없어 그 규격(28 viewBox·두톤·같은 선 굵기)대로 그렸다. 담을 것 4종 중 하나. */
export function DuoCompany(p: DuotoneIconProps): JSX.Element {
  return (
    <Duo {...p}>
      <path d="M4.4 25V5.2a1.6 1.6 0 0 1 1.6-1.6h9a1.6 1.6 0 0 1 1.6 1.6V25z" fill={INK} />
      <path d="M16.6 10.6h5.4a1.6 1.6 0 0 1 1.6 1.6V25h-7z" fill={KEY} />
      <path d="M2.6 25h22.8" stroke={INK} strokeWidth="2.2" strokeLinecap="round" />
      <path d="M8 8.4h4.4M8 13h4.4M8 17.6h4.4" stroke={PAPER} strokeWidth="1.6" strokeLinecap="round" />
    </Duo>
  );
}

/** 인사 — `icons:icon-58`. 사용자 관리 자리. */
export function DuoHr(p: DuotoneIconProps): JSX.Element {
  return (
    <Duo {...p}>
      <circle cx="13.4" cy="8.2" r="4.8" fill={INK} />
      <path d="M4.2 24.4c0-5.2 4.1-8.2 9.2-8.2 1.6 0 3.1.3 4.4.9" stroke={INK} strokeWidth="2.4" strokeLinecap="round" />
      <circle cx="20.6" cy="19.6" r="4.8" fill={KEY} />
      <path d="M18.5 19.6l1.5 1.5 2.7-2.9" stroke={PAPER} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </Duo>
  );
}

/** 알림 — `icons:icon-52`. */
export function DuoBell(p: DuotoneIconProps): JSX.Element {
  return (
    <Duo {...p}>
      <path d="M14 3.6a7.4 7.4 0 0 1 7.4 7.4v4.6l2 3.4H4.6l2-3.4V11A7.4 7.4 0 0 1 14 3.6z" fill={INK} />
      <path d="M11 21.4a3 3 0 0 0 6 0" stroke={KEY} strokeWidth="2.2" strokeLinecap="round" />
      <circle cx="21.2" cy="6.6" r="3" fill={KEY} />
    </Duo>
  );
}

/** 워크스페이스 — `icons:icon-16`(Hiworks `biz-ico_file.svg`). 원본 좌표계를 그대로 둔다. */
export function DuoWorkspace(p: DuotoneIconProps): JSX.Element {
  return (
    <Duo {...p}>
      <g transform="translate(-648 -1503)">
        <g transform="translate(499 -1373)">
          <g transform="translate(149 2876)">
            <g transform="translate(0 0.051)">
              <g transform="translate(0 0.933)">
                <path d="M843.84,686.533h-.373a.933.933,0,0,1,0-1.867h.373a4.231,4.231,0,0,0,4.293-4.157,4.15,4.15,0,0,0-2.177-3.616l-.559-.307.083-.632a4.071,4.071,0,0,0,.04-.537,4.231,4.231,0,0,0-4.293-4.157,4.344,4.344,0,0,0-1.856.414l-.871.407-.381-.883a5.6,5.6,0,0,0-5.168-3.333,5.524,5.524,0,0,0-5.6,5.43,5.291,5.291,0,0,0,.19,1.376l.223.831-.809.29a4.608,4.608,0,0,0-3.089,4.292,4.662,4.662,0,0,0,4.728,4.582.933.933,0,0,1,0,1.867A6.531,6.531,0,0,1,822,680.085a6.456,6.456,0,0,1,3.568-5.723,7.012,7.012,0,0,1-.084-1.065,7.392,7.392,0,0,1,7.467-7.3,7.487,7.487,0,0,1,6.476,3.66,6.185,6.185,0,0,1,1.8-.265,6.1,6.1,0,0,1,6.16,6.024c0,.055,0,.111,0,.167A5.993,5.993,0,0,1,850,680.51,6.1,6.1,0,0,1,843.84,686.533Z" transform="translate(-822 -666)" fill={INK} />
                <path d="M839.193,686.874a.932.932,0,0,0-1.32,0l-1.207,1.207v-6.147a.933.933,0,1,0-1.867,0v6.147l-1.207-1.207a.933.933,0,0,0-1.32,1.32l2.8,2.8c.008.008.02.011.028.019a.941.941,0,0,0,.274.184h0a.928.928,0,0,0,.711,0h0a.942.942,0,0,0,.274-.184c.008-.008.02-.01.028-.019l2.8-2.8A.932.932,0,0,0,839.193,686.874Z" transform="translate(-822.667 -664.667)" fill={INK} />
                <path d="M838.273,679.393a.932.932,0,0,0,1.32,0l1.207-1.207v6.147a.933.933,0,1,0,1.867,0v-6.147l1.207,1.207a.933.933,0,1,0,1.32-1.32l-2.8-2.8c-.008-.008-.02-.011-.028-.019a.941.941,0,0,0-.274-.184h0a.927.927,0,0,0-.711,0h0a.942.942,0,0,0-.274.184c-.008.008-.02.01-.028.019l-2.8,2.8A.932.932,0,0,0,838.273,679.393Z" transform="translate(-824.233 -666.6)" fill={KEY} />
              </g>
            </g>
          </g>
        </g>
      </g>
    </Duo>
  );
}

/** 예약 — `icons:icon-21`(Hiworks `biz-ico_reservation.svg`). */
export function DuoReservation(p: DuotoneIconProps): JSX.Element {
  return (
    <Duo {...p} viewBox="0 0 27.999 28.001">
      <g transform="translate(-412 -1503)">
        <g transform="translate(-562.848 931.732)">
          <path d="M1002.847,584.268h-13v-13h.982a12.026,12.026,0,0,1,12.018,12.018ZM991.813,582.3h9.024a10.06,10.06,0,0,0-9.024-9.023Z" fill={KEY} />
          <path d="M987.819,599.269a12.979,12.979,0,0,1-.844-25.93l1.061-.07v12.812h12.812l-.07,1.062a13.016,13.016,0,0,1-12.1,12.1C988.393,599.259,988.1,599.269,987.819,599.269Zm-1.776-23.821a10.986,10.986,0,1,0,12.625,12.625H986.043Z" fill={INK} />
        </g>
      </g>
    </Duo>
  );
}

/** 게시판 — `icons:icon-17`(Hiworks `biz-ico_bbs.svg`). */
export function DuoBoard(p: DuotoneIconProps): JSX.Element {
  return (
    <Duo {...p}>
      <g transform="translate(-474.002 -507.001)">
        <path d="M159.848,599.269h-24a1,1,0,0,1-1-1v-26a1,1,0,0,1,1-1h24a1,1,0,0,1,1,1v26A1,1,0,0,1,159.848,599.269Zm-23-2h22v-24h-22Z" transform="translate(340.154 -64.268)" fill={INK} />
        <path d="M152.115,578.269H140.85a1,1,0,0,1,0-2h11.267a1,1,0,1,1,0,2Z" transform="translate(340.154 -64.268)" fill={KEY} />
        <path d="M146.05,583.057h-5.2a1,1,0,0,1,0-2h5.2a1,1,0,0,1,0,2Z" transform="translate(340.154 -64.268)" fill={KEY} />
      </g>
    </Duo>
  );
}

/**
 * 전체메뉴 — `icons:icon-74`. 재료함에 «업무 메뉴» 라는 같은 이름이 셋 있는데
 * (`icon-73` 대시보드 · `icon-74` 전체메뉴 · `icon-4` 프로젝트), **siblingText 로 갈라야**
 * 맞는 것을 집는다. 이름·크기·출처가 셋 다 같다.
 *
 * 이 자리만 컴포넌트인 이유 — 좌측 레일의 다른 아이콘은 메뉴 레지스트리가 SVG 를 실어 오지만
 * 전체메뉴는 **레지스트리 메뉴가 아니라 프론트 버튼**이라 여기서 그린다.
 */
export function DuoAllMenu(p: DuotoneIconProps): JSX.Element {
  return (
    <Duo {...p}>
      <circle cx="6" cy="6" r="2.4" fill={INK} />
      <circle cx="14" cy="6" r="2.4" fill={KEY} />
      <circle cx="22" cy="6" r="2.4" fill={INK} />
      <circle cx="6" cy="14" r="2.4" fill={KEY} />
      <circle cx="14" cy="14" r="2.4" fill={INK} />
      <circle cx="22" cy="14" r="2.4" fill={KEY} />
      <circle cx="6" cy="22" r="2.4" fill={INK} />
      <circle cx="14" cy="22" r="2.4" fill={KEY} />
      <circle cx="22" cy="22" r="2.4" fill={INK} />
    </Duo>
  );
}

/** 캘린더 — `icons:icon-3`(Hiworks `biz-ico_schedule.svg`). 캘린더 테마 자리. */
export function DuoCalendar(p: DuotoneIconProps): JSX.Element {
  return (
    <Duo {...p}>
      <g transform="translate(-884 -123)">
        <path d="M561.5,359.585h-26a1,1,0,0,1-1-1v-20a1,1,0,0,1,1-1h26a1,1,0,0,1,1,1v20A1,1,0,0,1,561.5,359.585Zm-25-2h24v-18h-24Z" transform="translate(349.5 -211.585)" fill={INK} />
        <path d="M547.5,352.584a.993.993,0,0,1-.707-.293l-3-3a1,1,0,1,1,1.389-1.438l.025.024,2.293,2.293,5.293-5.293a1,1,0,0,1,1.439,1.39l-.025.024-6,6A1,1,0,0,1,547.5,352.584Z" transform="translate(349.5 -211.585)" fill={KEY} />
        <rect width="4" height="2" transform="translate(890.999 124)" fill={INK} />
        <rect width="4" height="2" transform="translate(901.003 124)" fill={INK} />
      </g>
    </Duo>
  );
}
